import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { toastWarning, toastSuccess } from "@/utils/toast";
import { createExtract, getStaffConfig } from "@/api/staff";
import { useWorkspaceStore } from "@/store/useWorkspaceStore";

import type { StaffWithdrawQuery } from "../types";
import { WithdrawType } from "./components/WithdrawType";
import { UploadPayImg } from "./components/UploadPayImg";
import { BankMessage } from "./components/BankMessage";

const BRAND = "#FF6B35";
const BRAND_GRADIENT = "linear-gradient(135deg, #FF6B35, #FF9A5C)";

export default function WithdrawPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const config = useWorkspaceStore((s) => s.config);
  const setConfig = useWorkspaceStore((s) => s.setConfig);

  const search = new URLSearchParams(location.search || "");
  const hasWithdrawMoney = (() => {
    const fromQuery = search.get("money");
    if (fromQuery && !Number.isNaN(Number(fromQuery))) return fromQuery;
    return (location.state as { money?: string })?.money ?? "0.00";
  })();

  const [query, setQuery] = useState<StaffWithdrawQuery>({
    extract_price: "",
    extract_type: "weixin",
    qrcode_url: "",
    bank_address: "",
    real_name: "",
    bank_code: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const updateQuery = (patch: Partial<StaffWithdrawQuery>) =>
    setQuery((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    if (config) return;
    (async () => {
      try {
        const c = await getStaffConfig();
        setConfig(c);
      } catch {
        // 错误提示在 request 层统一处理
      }
    })();
  }, [config, setConfig]);

  const handleSubmit = async () => {
    if (parseFloat(query.extract_price) <= 0) {
      toastWarning("提现金额不能小于0");
      return;
    }
    if (query.extract_type === "bank") {
      if (!query.bank_address || !query.bank_code) {
        toastWarning("请填写银行卡信息");
        return;
      }
      if (!String(query.real_name).trim()) {
        toastWarning("请填写持卡人真实姓名");
        return;
      }
    }
    if (
      (query.extract_type === "alipay" || query.extract_type === "weixin") &&
      !query.qrcode_url
    ) {
      toastWarning("请上传收款码");
      return;
    }

    setSubmitting(true);
    try {
      await createExtract({
        extract_price: query.extract_price,
        extract_type: query.extract_type,
        qrcode_url: query.qrcode_url || undefined,
        bank_address: query.bank_address || undefined,
        bank_code: query.bank_code || undefined,
        real_name: query.real_name || undefined,
      });
      toastSuccess("提现成功");
      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      console.error("[Withdraw Error]", err);
      const anyErr = err as any;
      const msg =
        anyErr?.response?.data?.message ||
        anyErr?.response?.data?.msg ||
        (typeof anyErr?.message === "string" ? anyErr.message : "") ||
        "";
      toastWarning(msg || "提现失败，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+96px)] overflow-x-hidden"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      {/* Header */}
      <div
        className="bg-white px-5 flex items-center gap-3"
        style={{
          paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)",
          paddingBottom: 13,
          borderBottom: "1px solid #F5F5F5",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] transition-transform shrink-0"
          style={{ background: "#F3F4F6" }}
        >
          <i
            className="ri-arrow-left-line"
            style={{ fontSize: 20, color: "#374151" }}
          />
        </button>
        <span className="text-lg font-bold text-gray-800">提现</span>
      </div>

      <div className="px-4 pt-4 space-y-3">
        {/* 可提现金额 */}
        <div
          className="bg-white rounded-2xl p-5"
          style={{ background: BRAND_GRADIENT }}
        >
          <span className="text-sm text-white/80">可提现收益(元)</span>
          <div className="text-4xl font-bold text-white mt-2">
            {hasWithdrawMoney}
          </div>
        </div>

        {/* 提现金额 */}
        <div className="bg-white rounded-2xl p-5">
          <span className="text-base font-bold text-gray-900">提现金额</span>
          <div className="mt-4 flex items-center">
            <span className="text-3xl font-bold text-gray-800">¥</span>
            <input
              type="number"
              value={query.extract_price}
              onChange={(e) => updateQuery({ extract_price: e.target.value })}
              placeholder="请输入提现金额"
              className="flex-1 text-3xl font-bold outline-none ml-1 bg-transparent"
            />
            <button
              onClick={() => updateQuery({ extract_price: hasWithdrawMoney })}
              className="text-sm font-semibold px-3 py-1.5 rounded-full active:scale-[0.98] transition-all shrink-0"
              style={{ color: BRAND, background: "#FFF3EE" }}
            >
              全部提现
            </button>
          </div>
        </div>

        {/* 提现方式 */}
        <WithdrawType value={query} onChange={updateQuery} />

        {/* 收款码（微信/支付宝） */}
        {(query.extract_type === "alipay" ||
          query.extract_type === "weixin") && (
          <UploadPayImg value={query} onChange={updateQuery} />
        )}

        {/* 银行卡信息 */}
        {query.extract_type === "bank" && (
          <BankMessage value={query} onChange={updateQuery} />
        )}
      </div>

      {/* 底部提交按钮 */}
      <div
        className="fixed inset-x-0 bottom-0 p-4 bg-[#F7F7F7] box-border z-50"
        style={{ width: "100%", maxWidth: "100vw", paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className={`w-full py-3.5 rounded-2xl text-sm font-semibold text-white cursor-pointer active:scale-[0.98] transition-all ${
            submitting ? "opacity-60" : ""
          }`}
          style={{ background: BRAND_GRADIENT }}
        >
          {submitting ? "提交中..." : "确认提现"}
        </button>
      </div>
    </div>
  );
}
